import React, { useEffect, useState } from 'react';
import { Instagram } from 'lucide-react';
import { OFFICIAL_INSTAGRAM_URL } from '@/config/siteOrganization';
import type { InstagramFeedData, InstagramFeedItem } from '@/types/instagramFeed';
import { fetchInstagramFeed } from '@/utils/fetchInstagramFeed';

function FeedImage({ item }: { item: InstagramFeedItem }) {
  const src = item.mediaType === 'VIDEO' ? item.thumbnailUrl || item.mediaUrl : item.mediaUrl;
  const label = item.caption
    ? item.caption.slice(0, 80)
    : 'Instagram の投稿（新しいタブで開く）';

  return (
    <a
      href={item.permalink}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block aspect-[4/5] overflow-hidden rounded-lg bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
      aria-label={label}
    >
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
      />
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/35">
        <Instagram
          className="h-8 w-8 text-white opacity-0 transition-opacity group-hover:opacity-100"
          aria-hidden
        />
      </span>
      {item.mediaType === 'VIDEO' && (
        <span className="absolute right-2 top-2 rounded bg-black/60 px-2 py-0.5 text-xs text-white">
          動画
        </span>
      )}
      {item.mediaType === 'CAROUSEL_ALBUM' && (
        <span className="absolute right-2 top-2 rounded bg-black/60 px-2 py-0.5 text-xs text-white">
          複数
        </span>
      )}
    </a>
  );
}

function FeedSkeleton() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4" aria-hidden>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="aspect-[4/5] rounded-lg bg-gray-800 animate-pulse" />
      ))}
    </div>
  );
}

export default function InstagramFeed() {
  const [feed, setFeed] = useState<InstagramFeedData | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchInstagramFeed().then((data) => {
      if (!cancelled) setFeed(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const profileUrl = feed?.profileUrl || OFFICIAL_INSTAGRAM_URL;
  const hasItems = (feed?.items.length ?? 0) > 0;

  return (
    <section
      className="border-b border-gray-700 pb-8 sm:pb-10 mb-8 sm:mb-10"
      aria-labelledby="instagram-feed-heading"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h2
            id="instagram-feed-heading"
            className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2"
          >
            <Instagram className="h-6 w-6 text-pink-400" aria-hidden />
            Instagram
          </h2>
          <p className="text-gray-400 text-sm mt-1">@socialtrip.ai</p>
        </div>
        <a
          href={profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-pink-400/50 px-4 py-2 text-sm font-medium text-white hover:bg-pink-500/10 transition-colors min-h-10"
        >
          <Instagram className="h-4 w-4" aria-hidden />
          公式アカウントを見る
        </a>
      </div>

      {!feed && <FeedSkeleton />}

      {feed && hasItems && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          {feed.items.map((item) => (
            <FeedImage key={item.id} item={item} />
          ))}
        </div>
      )}

      {feed && !hasItems && (
        <div className="rounded-xl border border-dashed border-gray-600 bg-gray-800/40 px-4 py-8 text-center">
          <p className="text-gray-300 text-sm">
            Instagram の連携設定後、ここに最新投稿が表示されます。
          </p>
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 text-pink-300 hover:text-pink-200 text-sm font-medium"
          >
            <Instagram className="h-4 w-4" aria-hidden />
            いまは Instagram でご覧ください
          </a>
        </div>
      )}
    </section>
  );
}
